import { Component, OnDestroy, OnInit } from '@angular/core';
import { Observable, Subscription, combineLatest, filter, map, merge, of, switchMap, takeUntil, timer } from 'rxjs';
import { Store } from '@ngrx/store';

import { UserSettingsEntityActions } from '../../store/user-settings-entity/user-settings-entity.actions';
import { selectLoading, selectUserSettingsEntity } from '../../store/user-settings-entity/user-settings-entity.selectors';
import type { UserSettings } from 'src/app/core/models/user-settings.model';
import { MatDialog } from '@angular/material/dialog';
import { selectUserSettings } from '../../store/user-settings/user-settings.selectors';
import { DeactivateDialogComponent } from 'src/app/shared/components/deactivate-dialog/deactivate-dialog.component';
import type { Breadcrumb } from 'src/app/core/models/breadcrumb.model';
import type { Entity } from '../../../core/models/entity.model';
import type { DeactivatableComponent } from '../../../core/guards/deactivatable.component.interface';
import { selectMessageEntities } from 'src/app/core/store/messages/messages.selectors';

@Component({
  selector: 'app-user-settings-view',
  templateUrl: './user-settings-view.component.html',
  styleUrls: ['./user-settings-view.component.scss'],
  standalone: false
})
export class UserSettingsViewComponent implements OnInit, OnDestroy, DeactivatableComponent {
  userSettingsEntity$: Observable<Entity>;
  loading$: Observable<boolean>;
  showLoading$ = of(false);
  userSettings$: Observable<UserSettings>;
  breadcrumbs: Breadcrumb[];
  messages: Entity[] = [];
  messageSubscription: Subscription;

  constructor(private store: Store, public dialog: MatDialog) {
    this.userSettingsEntity$ = this.store.select(selectUserSettingsEntity);
    this.loading$ = this.store.select(selectLoading);
    this.userSettings$ = this.store.select(selectUserSettings);
    const resultReceived = this.loading$.pipe(filter(result => !result));

    this.showLoading$ = merge(
      timer(350).pipe(
        map(() => true),
        takeUntil(resultReceived)
      ),
      resultReceived.pipe(map(() => false))
    );
    this.breadcrumbs = [
      { url: '/projects', text: 'UI.Common.Breadcrumb.Projects', translatable: true, isCurrent: false },
      { url: '/projects/user-settings', text: 'UI.Common.Breadcrumb.UserSettings', translatable: true, isCurrent: true }
    ];
  }

  ngOnInit() {
    this.store.dispatch(UserSettingsEntityActions.get_entity_start());

    this.messageSubscription = combineLatest([this.store.select(selectMessageEntities), this.userSettingsEntity$]).subscribe(
      ([messageEntities, userSettingsEntity]) => {
        this.messages = messageEntities.filter(x => x.ParentId === userSettingsEntity.Id);
      }
    );
  }

  ngOnDestroy(): void {
    this.messageSubscription.unsubscribe();
  }

  containsUnsavedChanges() {
    return combineLatest([this.userSettingsEntity$, this.userSettings$]).pipe(
      map(data => {
        const entityFormValues = data[0].Properties;
        const latestSavedValue = data[1];
        let formIsDirty = false;
        Object.keys(latestSavedValue).forEach(key => {
          const propertyValue = entityFormValues[key].Value;
          if (latestSavedValue[key] !== propertyValue) {
            formIsDirty = true;
          }
        });
        return formIsDirty;
      })
    );
  }

  canDeactivate(): boolean | Observable<boolean> {
    return this.containsUnsavedChanges().pipe(
      switchMap(value => {
        if (!value) {
          return of(true);
        } else {
          return this.dialog
            .open(DeactivateDialogComponent, { disableClose: false, hasBackdrop: true })
            .afterClosed()
            .pipe(
              map(data => {
                if (data === undefined) {
                  return false;
                }
                return data;
              })
            );
        }
      })
    );
  }
}
